// PowerSync — реактивные хуки для flashcards/sets.
//
// Local*-типы — РЕАЛЬНЫЕ колонки БД (information_schema). flashcard_sets.name
// (не title), processing_status (не status). flashcards имеет реальный user_id.
// jsonb/ARRAY → TEXT (JSON-строка) на клиенте.
import { usePowerSyncQuery } from "@/lib/powersync/use-query";

export interface LocalFlashcardSet {
	id: string;
	user_id: string;
	name: string;
	description: string | null;
	color: string | null;
	generated_from_material_id: string | null;
	processing_status: string;
	generated_by_ai: number; // bool
	flashcards_count: number | null;
	due_count: number | null;
	new_count: number | null;
	language: string | null;
	ai_model: string | null;
	prompt_version: string | null;
	generation_trace_id: string | null;
	created_at: string;
	updated_at: string;
	deleted_at: string | null;
	/** LEFT JOIN materials.name (список). */
	material_name?: string | null;
	/** LEFT JOIN materials.source_kind (список). */
	material_source_kind?: string | null;
}

export interface LocalFlashcard {
	id: string;
	set_id: string;
	user_id: string;
	type: string;
	front: string;
	back: string;
	hint: string | null;
	options: string | null; // ARRAY → JSON
	correct_answer: string | null;
	audio_url: string | null;
	audio_cache_key: string | null;
	front_image_url: string | null;
	back_image_url: string | null;
	back_caption: string | null;
	srs_state: string;
	srs_interval: number;
	srs_ease_factor: number;
	srs_repetition_count: number;
	srs_lapse_count: number;
	srs_consecutive_again: number | null;
	srs_due_at: string | null;
	srs_last_reviewed_at: string | null;
	srs_last_rating: string | null;
	marked: number;
	marked_consecutive_correct: number;
	correct_count: number;
	incorrect_count: number;
	generated_by_ai: number;
	ai_model: string | null;
	prompt_version: string | null;
	generation_trace_id: string | null;
	citation: string | null; // jsonb
	tags: string | null; // ARRAY → JSON
	created_at: string;
	updated_at: string;
	deleted_at: string | null;
}

/** Карточка + денормализованные поля набора (marked / due virtual sets). */
export interface LocalFlashcardWithSet extends LocalFlashcard {
	set_name: string | null;
	set_color: string | null;
}

export function useFlashcardSetsQuery(userId: string | undefined) {
	return usePowerSyncQuery<LocalFlashcardSet>(
		`SELECT s.*, m.name AS material_name, m.source_kind AS material_source_kind
		   FROM flashcard_sets s
		   LEFT JOIN materials m ON m.id = s.generated_from_material_id
		  WHERE s.user_id = ? AND s.deleted_at IS NULL
		ORDER BY s.updated_at DESC`,
		[userId ?? ""],
	);
}

export function useFlashcardSetByIdQuery(setId: string | undefined) {
	return usePowerSyncQuery<LocalFlashcardSet>(
		`SELECT s.*, m.name AS material_name, m.source_kind AS material_source_kind
		   FROM flashcard_sets s
		   LEFT JOIN materials m ON m.id = s.generated_from_material_id
		  WHERE s.id = ? AND s.deleted_at IS NULL`,
		[setId ?? ""],
	);
}

export function useFlashcardsBySetQuery(setId: string | undefined) {
	return usePowerSyncQuery<LocalFlashcard>(
		`SELECT * FROM flashcards
		   WHERE set_id = ? AND deleted_at IS NULL
		ORDER BY sort_order ASC, created_at ASC`,
		[setId ?? ""],
	);
}

/** Карточки с наступившим сроком SRS (srs_due_at <= now), без suspended/buried. */
export function useDueFlashcardsQuery(userId: string | undefined) {
	const nowIso = new Date().toISOString();
	return usePowerSyncQuery<LocalFlashcardWithSet>(
		`SELECT f.*, s.name AS set_name, s.color AS set_color
		   FROM flashcards f
		   JOIN flashcard_sets s ON s.id = f.set_id
		  WHERE s.user_id = ? AND s.deleted_at IS NULL AND f.deleted_at IS NULL
		    AND f.srs_due_at IS NOT NULL
		    AND f.srs_due_at <= ?
		    AND f.srs_state NOT IN ('suspended', 'buried')
		ORDER BY f.srs_due_at ASC`,
		[userId ?? "", nowIso],
	);
}

/** Cross-set помеченные карточки (marked = 1). */
export function useMarkedFlashcardsQuery(userId: string | undefined) {
	return usePowerSyncQuery<LocalFlashcardWithSet>(
		`SELECT f.*, s.name AS set_name, s.color AS set_color
		   FROM flashcards f
		   JOIN flashcard_sets s ON s.id = f.set_id
		  WHERE s.user_id = ? AND s.deleted_at IS NULL AND f.deleted_at IS NULL
		    AND f.marked = 1
		ORDER BY f.updated_at DESC`,
		[userId ?? ""],
	);
}
